﻿import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Paper,
  Typography,
} from "@mui/material";
import {
  FieldPath,
  FieldValues,
  get,
  UseControllerProps,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { FilesList } from "components/FilesList";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];

type FormFileUploadProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  label?: string;
  required?: boolean;
  maxFiles?: number;
};

export const FormFileUpload = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  label,
  required,
  maxFiles = 5,
}: FormFileUploadProps<TFieldValues, TName>) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    field: { value, onChange, ref },
    formState,
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  // Filter out null/undefined values from files array (can happen after deserialization)
  const files = (value ?? []).filter(
    (file) => file !== null && file !== undefined
  );
  const errorMsg = get(formState.errors, name)?.message;

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t("details.specialRequirements.files.error.fileSize", {
        maxSize: "5MB",
      });
    }

    // Check file extension
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return t("details.specialRequirements.files.error.fileType", {
        allowedTypes: ALLOWED_EXTENSIONS.join(", "),
      });
    }

    return null;
  };

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newFiles = Array.from(selectedFiles);
    const currentFiles = files || [];

    // Clear previous upload error
    setUploadError(null);

    // Check max files limit
    if (currentFiles.length + newFiles.length > maxFiles) {
      setUploadError(
        t("details.specialRequirements.files.error.tooManyFiles", {
          maxFiles,
        })
      );
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of newFiles) {
      const error = validateFile(file);
      if (!error) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${error}`);
      }
    }

    // Show errors if any files were rejected
    if (errors.length > 0) {
      setUploadError(
        t("details.specialRequirements.files.error.invalidFiles", {
          count: errors.length,
        }) +
          "\n" +
          errors.join("\n")
      );
    }

    // Add valid files
    if (validFiles.length > 0) {
      onChange([...currentFiles, ...validFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onChange(updatedFiles);
    // Clear upload error when user removes a file
    setUploadError(null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleFileSelect(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  return (
    <FormControl fullWidth error={!!errorMsg}>
      <FormLabel htmlFor={name} required={required}>
        {label}
      </FormLabel>

      {files.length >= maxFiles ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t("details.specialRequirements.files.maxFilesReached", { maxFiles })}
        </Alert>
      ) : (
        <Paper
          variant="outlined"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            p: 3,
            mt: 1,
            textAlign: "center",
            backgroundColor: "background.default",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: errorMsg ? "error.main" : "divider",
            cursor: "pointer",
            "&:hover": {
              borderColor: errorMsg ? "error.dark" : "primary.main",
              backgroundColor: "action.hover",
            },
          }}
          onClick={handleButtonClick}
        >
          <input
            ref={(instance) => {
              ref(instance);
              inputRef.current = instance;
            }}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ opacity: 0, width: 0, height: 0 }}
            onChange={(e) => handleFileSelect(e.target.files)}
          />

          <UploadFileIcon
            sx={{ fontSize: 48, color: "action.active", mb: 1 }}
          />
          <Typography variant="body1" gutterBottom>
            {t("details.specialRequirements.files.dragDrop")}
          </Typography>
          <Typography
            variant="body2"
            gutterBottom
            sx={{ color: "text.secondary" }}
          >
            {t("details.specialRequirements.files.or")}
          </Typography>
          <Button variant="contained" component="span" sx={{ mt: 1 }}>
            {t("details.specialRequirements.files.selectFiles")}
          </Button>
          <Typography variant="caption" sx={{ display: "block", mt: 2 }}>
            {t("details.specialRequirements.files.requirements", {
              maxFiles,
              maxSize: "5MB",
              formats: "PDF, JPG, JPEG, PNG",
            })}
          </Typography>
        </Paper>
      )}

      {uploadError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          onClose={() => setUploadError(null)}
        >
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {uploadError}
          </Typography>
        </Alert>
      )}

      <FilesList files={files} onFileRemove={handleRemoveFile} />

      {errorMsg && (
        <FormHelperText error sx={{ mt: 1 }}>
          {errorMsg}
        </FormHelperText>
      )}
    </FormControl>
  );
};
